/**
 * Copyright 2020 New Light Technologies, Inc.
 *
 * With Supporting Sponsorship from the Federal Emergency Management Agency (Contract: GSA Stars II GS-06F-0968Z)
 * In accordance with FAR 52.227-14(c)(iii), New Light Technologies, Inc. grants to the Government and others acting on its behalf a paid-up, nonexclusive, irrevocable, worldwide license in such copyrighted computer software and data to reproduce, prepare derivative works, and perform publicly and display publicly (but not to distribute copies to the public) by or on behalf of the Government.
 * Any other use, distribution, reproduction, modification, or publication without the prior express written authorization of New Light Technologies, Inc. is strictly prohibited.
 * All other rights are reserved by their respective copyright holders.
 *
 */
import React, { useState, useEffect } from 'react';
import './App.css';
import Layout from './components/Layout';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  Outlet,
  useParams,
  useLocation,
} from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { Provider, useAtom } from 'jotai';
import { theme } from './theme';
import { MapPage } from './pages/MapPage';
import SubscriptionPage from './subscriptions/SubscriptionPage';
import SubscriptionManagePage from './subscriptions/SubscriptionManagePage';
import { WowcastPage } from './pages/WowcastPage';
import { IncidentcastPage } from './pages/IncidentcastPage';
import LoginPage from './pages/LoginPage';
import { userAtom } from './auth/userAtoms';
import { AuthStateManager } from './auth/AuthStateManager';
import { Box, CircularProgress } from '@mui/material';
import { getCsrfToken } from './services/arcgisProxy';
import { ErrorBoundary } from './components/ErrorBoundary';

const ALLOWED_GROUP_IDS = [
  '7098b2ef3e2048ffbed58dee5af632eb', // TEMPO
  'ad2af39fad9f40d2921a65f01879e9d8', // Live App - Geospatial Damage Assessment
  '8e95e3c576304ab49f5fc8396f41484b', // NSARGC 3 SARCOP Partnered Collaboration Internal Group
  '2512a123c21f4373b7baf178bc6c96fb', // Management Team - Geospatial Damage Assessment
  'bd14c115aac443a1a7350d8510655fd9', // PhotoMappers
  '434db03082d14e89bd51c9e5bdde4ad4', // Incident Resources Group
];

interface PrivateRouteProps {
  children: React.ReactNode;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children }) => {
  const [user, setUser] = useAtom(userAtom);
  const location = useLocation();
  const [loading, setIsLoading] = useState(true);

  const hasNoAccess =
    user?.appInfo.orgId !== user?.orgId &&
    !user?.groups?.find(g => ALLOWED_GROUP_IDS.includes(g.id));

  useEffect(() => {
    // Add a small delay to prevent flash of login screen
    const timer = setTimeout(() => setIsLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (user && hasNoAccess) {
      fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-XSRF-TOKEN': getCsrfToken() },
      }).catch(err => console.error('Failed to destroy server session', err));
      setUser(null);
    }
  }, [user, setUser, hasNoAccess]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: '#f5f5f5',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!user || hasNoAccess) {
    return (
      <Navigate
        to={{
          pathname: '/login',
          search: location.search,
        }}
        state={{
          from: location,
          searchParams: location.search,
          showAccessDenied: hasNoAccess, // Pass this flag to LoginPage
        }}
        replace
      />
    );
  }

  return <>{children}</>;
};

const App = () => {
  return (
    <Provider>
      <ThemeProvider theme={theme}>
        {/* The last line of defence, immediately under the theme so the
            fallback still renders as an MUI alert: it catches anything the
            router, the auth gate or the shell itself throws, which is the only
            way those can fail as something other than a white screen. */}
        <ErrorBoundary region="Tempo">
          <React.Suspense fallback={null}>
            <BrowserRouter>
              <AuthStateManager>
                <Routes>
                  <Route path="/login" element={<LoginPage />} />
                  <Route
                    path="/"
                    element={
                      <PrivateRoute>
                        <Layout>
                          <div id="clouds">
                            <div className="cloud x1" />
                            <div className="cloud x2" />
                            <div className="cloud x3" />
                            <div className="cloud x4" />
                            <div className="cloud x5" />
                          </div>
                          <RoutedView />
                        </Layout>
                      </PrivateRoute>
                    }
                  >
                    <Route path="/" element={<MapPage />}>
                      {/* The incident map is the parent's element; this leaf only
                          makes the URL match, so it deliberately renders nothing. */}
                      <Route path=":incidentId/:sectionId/map" element={null} />
                      <Route
                        path=":incidentId/:sectionId"
                        element={<Navigate replace to="map" />}
                      />
                    </Route>
                    <Route path="wowcast" element={<WowcastPage />} />
                    <Route path="wowcast/:incidentId/:sectionId" element={<IncidentcastPage />} />
                    <Route path=":incidentId" element={<IncidentRedirect />} />
                  </Route>
                  <Route path="subscriptions/manage/:token" element={<SubscriptionManagePage />} />
                  <Route path="subscriptions" element={<SubscriptionPage />} />
                </Routes>
              </AuthStateManager>
            </BrowserRouter>
          </React.Suspense>
        </ErrorBoundary>
      </ThemeProvider>
    </Provider>
  );
};

/**
 * The routed view as its own failure domain: a view that throws leaves the
 * header, the footer and the navigation standing, so the user can still leave
 * the broken view. Keyed on the path so navigating -- to another incident, or
 * back to the dashboard -- clears a caught error rather than carrying the
 * fallback of the record that broke it to the next one.
 */
const RoutedView = () => {
  const location = useLocation();

  return (
    <ErrorBoundary region="This view" resetKey={location.pathname}>
      <Outlet />
    </ErrorBoundary>
  );
};

const IncidentRedirect = () => {
  const { incidentId, viewType } = useParams();

  if (incidentId && !viewType) {
    return <Navigate to={`/${incidentId}/population/map`} replace />;
  }

  return null;
};

export default App;
